<template>
  <modal
    class="participant-activity-log-modal"
    name="participant-activity-log-modal"
    @before-open="handleBeforeOpen"
    @before-close="handleBeforeClose"
    transition="fade"
    :adaptive="true"
    :scrollable="true"
    :reset="true"
    width="60%"
    height="auto"
    style="z-index: 4100;"
  >
    <div id="modal-header" style="background-color:#006BE6">
      <div class="w-75 d-flex text-white">
        <p>Փոփոխությունների պատմություն {{ lotLabel }}</p>
      </div>
      <button type="button"
              class="close position-absolute"
              style="right: 1.25rem; top: 25%"
              @click="$modal.hide('participant-activity-log-modal')" aria-label="Close">
        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M16.5 1.64102L14.859 0L8.49997 6.359L2.14102 4.62582e-05L0.5 1.64107L6.85895 8.00002L0.500034 14.3589L2.14105 16L8.49997 9.64104L14.8589 16L16.5 14.359L10.141 8.00002L16.5 1.64102Z" fill="#ffffff"/>
        </svg>
      </button>
    </div>
    <div id="modal-body" class="p-4">
      <div v-if="loading" class="text-center py-4">
        <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
        <span class="ml-2">Բեռնում...</span>
      </div>
      <div v-else-if="!logs.length" class="text-center py-4">
        <p class="text-muted mb-0">Փոփոխություններ չկան</p>
      </div>
      <div v-else class="activity-timeline">
        <div
          v-for="log in logs"
          :key="log.id"
          class="activity-item mb-3 p-3 bg-light br-8"
        >
          <div class="d-flex justify-content-between align-items-start mb-2">
            <div>
              <span class="badge" :class="eventBadgeClass(log.event)">
                {{ eventLabel(log.event) }}
              </span>
              <span class="badge ml-1" :class="subjectBadgeClass(log.subject_type)">
                {{ subjectLabel(log.subject_type) }}
              </span>
              <span v-if="log.causer && log.causer.username" class="ml-2 font-size-13 text-secondary">
                {{ log.causer.username }}
              </span>
            </div>
            <small class="text-muted">{{ formatDate(log.changed_at) }}</small>
          </div>
          <div v-if="log.changes && log.changes.length">
            <div
              v-for="(change, idx) in log.changes"
              :key="idx"
              class="change-row d-flex align-items-center mt-1"
            >
              <span class="field-badge mr-2">{{ fieldLabel(change.field) }}</span>
              <span class="old-val" :title="change.old_value">{{ change.old_value != null ? change.old_value : '—' }}</span>
              <svg class="mx-2 flex-shrink-0" width="12" height="12" viewBox="0 0 10 10" fill="none">
                <path d="M1 5h8M6 2l3 3-3 3" stroke="#6E7485" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="new-val" :title="change.new_value">{{ change.new_value != null ? change.new_value : '—' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="modal-footer" class="p-3 text-right">
      <button type="button" @click="$modal.hide('participant-activity-log-modal')" class="btn btn-secondary br-8">
        <span>Փակել</span>
      </button>
    </div>
  </modal>
</template>

<script>
export default {
  name: 'ParticipantRowActivityLogModal',
  data() {
    return {
      logs: [],
      loading: false,
      lotLabel: '',
    }
  },
  methods: {
    handleBeforeOpen({ params }) {
      const lotId = params?.lotId;
      this.lotLabel = params?.lotLabel || '';
      if (lotId) {
        this.fetchLogs(lotId);
      }
    },
    handleBeforeClose() {
      this.logs = [];
      this.loading = false;
      this.lotLabel = '';
    },
    async fetchLogs(lotId) {
      this.loading = true;
      try {
        const { data } = await this.$client.get(`participant-row/activity-logs/${lotId}`);
        this.logs = data?.data || [];
      } catch (e) {
        console.error('Failed to fetch activity logs', e);
        this.logs = [];
      } finally {
        this.loading = false;
      }
    },
    eventLabel(event) {
      return { created: 'Ստեղծված', updated: 'Թարմացված', deleted: 'Ջնջված' }[event] || event;
    },
    eventBadgeClass(event) {
      return {
        'badge-success':   event === 'created',
        'badge-warning':   event === 'updated',
        'badge-danger':    event === 'deleted',
        'badge-secondary': !['created', 'updated', 'deleted'].includes(event),
      }
    },
    subjectLabel(subject) {
      const labels = {
        'ParticipantRow': 'Գնային առաջարկ',
        'Participant': 'Առևտրային մասնակից',
      };
      return labels[subject] || subject || '';
    },
    subjectBadgeClass(subject) {
      return {
        'badge-info': subject === 'ParticipantRow',
        'badge-primary': subject === 'Participant',
      };
    },
    fieldLabel(field) {
      const labels = {
        value: 'Արժեք (առանց ԱԱՀ)',
        vat: 'ԱԱՀ',
        cost: 'Ընդհանուր գին',
        total_price: 'Ընդհանուր արժեք',
        prepayment_condition: 'Կանխավճարի պայմաններ',
        postpayment_condition: 'Հետվճարի պայմաններ',
        delivery_condition: 'Առաքման պայմաններ',
        delivery_type: 'Առաքման տեսակ',
      };
      return labels[field] || field;
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr.replace(' ', 'T'));
      if (isNaN(d)) return dateStr;
      const p = n => String(n).padStart(2, '0');
      return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
  },
}
</script>

<style scoped>
.br-8 {
  border-radius: 8px;
}
.field-badge {
  background: #e9ecef;
  color: #495057;
  border-radius: 3px;
  padding: 1px 6px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}
.old-val {
  color: #6c757d;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
}
.new-val {
  color: #006BE6;
  font-weight: 500;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
}
.activity-timeline {
  max-height: 400px;
  overflow-y: auto;
}
</style>